import {namespaced} from '@/store/uitls.js';
import * as Profile from '@/store/modules/profile.js';
import * as Application from '@/store/modules/application.js';
import * as ErrorContainer from '@/store/modules/ErrorContainer.js';
import * as PaymentTypes from '@/store/modules/additional-payment/modules/payment-types.js';
import * as AdditionalPayment from '@/store/modules/additional-payment/additional-payment.js';
import * as GracePeriod from '@store/modules/GracePeriod.js';
import * as Solicitor from '@store/modules/Solicitor.js';
import * as LoanState from '@store/modules/application/modules/LoanState.js';
import * as LoanAlert from '@store/modules/LoanAlert.js';
import * as BoughtLead from '@modules/guest/bought-lead/store/BoughtLead.js';
import * as NewApplication from '@modules/guest/new-applicant/store/modules/NewApplicant.js';
import * as ApprovalRejectionTypes from '@store/modules/application/modules/ApprovalRejectionTypes.js';

const moduleName = (...modules) => modules.map(({MODULE_NAME}) => MODULE_NAME);

/**
 * @template G extends Record<string, string>
 * @template M extends Record<string, string>
 * @template A extends Record<string, string>
 * @param {{
 *   MODULE_NAME: string,
 *   GETTERS: G,
 *   MUTATIONS: M,
 *   ACTIONS: A
 * }} options
 *   - MODULE_NAME: the namespace root (string or array of strings)
 *   - GETTERS: an object mapping getter‐keys to leaf strings
 *   - MUTATIONS: an object mapping mutation‐keys to leaf strings
 *   - ACTIONS: an object mapping action‐keys to leaf strings
 * @returns {{
 *   MODULE_NAME: string,
 *   GETTERS:   { [K in keyof G]: string },
 *   MUTATIONS: { [K in keyof M]: string },
 *   ACTIONS:   { [K in keyof A]: string }
 * }}
 *   Returns a new object with:
 *   - MODULE_NAME unchanged,
 *   - GETTERS/MUTATIONS/ACTIONS each “namespaced” (same keys as input, values become string).
 */
const transform = ({ MODULE_NAME, GETTERS, MUTATIONS, ACTIONS }) => {
  return {
    MODULE_NAME,
    GETTERS: namespaced(MODULE_NAME, GETTERS),
    MUTATIONS: namespaced(MODULE_NAME, MUTATIONS),
    ACTIONS: namespaced(MODULE_NAME, ACTIONS),
  };
};

export const APPLICATION = transform(Application);
export const APPROVAL_REJECTION_TYPES = transform(ApprovalRejectionTypes);
export const NEW_APPLICATION = transform(NewApplication);
export const LOAN_STATE = transform({
  ...LoanState,
  MODULE_NAME: moduleName(Application, LoanState),
});
export const ADDITIONAL_PAYMENT = transform(AdditionalPayment);
export const ERROR_CONTAINER = transform(ErrorContainer);
export const GRACE_PERIOD = transform(GracePeriod);
export const PAYMENT_TYPES = transform({
  ...PaymentTypes,
  MODULE_NAME: moduleName(AdditionalPayment, PaymentTypes),
});
export const PROFILE = transform(Profile);
export const SOLICITOR = transform(Solicitor);
export const LOAN_ALERT = transform(LoanAlert);
export const BOUGHT_LEAD = transform(BoughtLead);

export const STORE_MODULE_CONSTANTS = {
  APPLICATION,
  NEW_APPLICATION,
  ADDITIONAL_PAYMENT,
  ERROR_CONTAINER,
  GRACE_PERIOD,
  PAYMENT_TYPES,
  PROFILE,
  SOLICITOR,
  LOAN_ALERT,
  BOUGHT_LEAD,
  APPROVAL_REJECTION_TYPES,
};

export default STORE_MODULE_CONSTANTS;
